import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  Typography,
} from "@mui/material";

function NewFolderDialog({ open, onClose, onSave, existingFolders, initialParent = "" }) {
  const [folderName, setFolderName] = useState("");
  const [parentFolder, setParentFolder] = useState(initialParent);

  useEffect(() => {
    setParentFolder(initialParent);
  }, [initialParent]);

  // Derive unique Level 1 folder names from existing folder paths
  const parentOptions = useMemo(() => {
    if (!existingFolders) return [];
    const level1 = new Set();
    existingFolders.forEach((f) => {
      if (f && f.length > 0) {
        level1.add(f[0]);
      }
    });
    return Array.from(level1).sort();
  }, [existingFolders]);

  const handleSave = () => {
    const trimmed = folderName.trim();
    if (!trimmed) return;

    const newPath = parentFolder ? [parentFolder, trimmed] : [trimmed];
    
    // Validate max 2 levels
    if (newPath.length > 2) return;
    
    // Check for duplicates
    const pathStr = newPath.join(" / ");
    const isDuplicate = existingFolders?.some(
      (f) => Array.isArray(f) && f.join(" / ") === pathStr
    );
    if (isDuplicate) return;

    onSave(newPath);
    setFolderName("");
    setParentFolder("");
    onClose();
  };

  const handleClose = () => {
    setFolderName("");
    setParentFolder("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Folder</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            autoFocus
            label="Folder Name"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            fullWidth
            placeholder="e.g., Landscapes"
          />
          {parentOptions.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>Parent Folder (optional)</InputLabel>
              <Select
                value={parentFolder}
                onChange={(e) => setParentFolder(e.target.value)}
                label="Parent Folder (optional)"
              >
                <MenuItem value="">
                  <em>None (top-level folder)</em>
                </MenuItem>
                {parentOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {parentFolder && folderName && (
            <Typography variant="body2" color="text.secondary">
              Path: {parentFolder} / {folderName.trim()}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!folderName.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NewFolderDialog;
